import React from 'react';
import { Activity } from 'lucide-react';
import { VitalSignTrendChart, VitalSignDataPoint } from '../../molecules/VitalSignTrendChart';
import { VitalSignsResponse, VitalSign } from '../../../types/residents';
import { VITALS_VISIBILITY } from '../../../config/vitalsConfig';

interface VitalSignsTrendsSectionProps {
  data: VitalSignsResponse | null;
  isLoading: boolean;
  error: Error | null;
}

function formatDateForChart(dateStr: string): string {
  // If the string contains a time portion, include it in the label
  if (dateStr.includes("T") && dateStr.length > 10) {
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      return `${month}/${day} ${hours}:${minutes}`;
    }
  }

  // Date-only fallback: parse YYYY-MM-DD or MM/DD/YYYY
  const datePart = dateStr.split("T")[0];
  if (datePart.includes("-")) {
    const parts = datePart.split("-");
    if (parts.length >= 3) {
      return `${parts[1]}/${parts[2].substring(0, 2)}`;
    }
  }
  if (datePart.includes("/")) {
    const parts = datePart.split("/");
    return `${parts[0]}/${parts[1]}`;
  }
  return dateStr;
}

// Null when there are not enough points to compute a direction — a single
// reading (or none) is not an upward trend.
function computeTrend(arr: VitalSignDataPoint[]): 'UP' | 'DOWN' | null {
  if (arr.length < 2) return null;
  return arr[arr.length - 1].value >= arr[0].value ? 'UP' : 'DOWN';
}

function transformVitalSigns(readings: VitalSign[], priorWeights: VitalSign[] = []) {
  const sorted = [...readings]
    .filter(r => !r.is_struck)
    .sort((a, b) => a.date.localeCompare(b.date));

  const bloodPressure: VitalSignDataPoint[] = [];
  const heartRate: VitalSignDataPoint[] = [];
  const temperature: VitalSignDataPoint[] = [];
  const oxygenSaturation: VitalSignDataPoint[] = [];
  const weight: VitalSignDataPoint[] = [];
  const respiration: VitalSignDataPoint[] = [];
  const painLevel: VitalSignDataPoint[] = [];
  const bmi: VitalSignDataPoint[] = [];
  const bloodGlucose: VitalSignDataPoint[] = [];

  for (const reading of sorted) {
    const dateLabel = formatDateForChart(reading.date);

    if (reading.blood_pressure) {
      const parts = reading.blood_pressure.split("/");
      if (parts.length === 2) {
        const systolic = parseInt(parts[0], 10);
        const diastolic = parseInt(parts[1], 10);
        if (!isNaN(systolic) && !isNaN(diastolic)) {
          bloodPressure.push({ date: dateLabel, rawDate: reading.date, value: systolic, value2: diastolic });
        }
      }
    }

    if (reading.heart_rate !== null && reading.heart_rate !== undefined) {
      heartRate.push({ date: dateLabel, rawDate: reading.date, value: reading.heart_rate });
    }
    if (reading.temperature !== null && reading.temperature !== undefined) {
      temperature.push({ date: dateLabel, rawDate: reading.date, value: reading.temperature });
    }
    if (reading.oxygen_saturation !== null && reading.oxygen_saturation !== undefined) {
      oxygenSaturation.push({ date: dateLabel, rawDate: reading.date, value: reading.oxygen_saturation });
    }
    if (reading.weight !== null && reading.weight !== undefined) {
      weight.push({ date: dateLabel, rawDate: reading.date, value: reading.weight });
    }
    if (reading.respiration !== null && reading.respiration !== undefined) {
      respiration.push({ date: dateLabel, rawDate: reading.date, value: reading.respiration });
    }
    if (reading.pain_level !== null && reading.pain_level !== undefined) {
      painLevel.push({ date: dateLabel, rawDate: reading.date, value: reading.pain_level });
    }
    if (reading.bmi !== null && reading.bmi !== undefined) {
      bmi.push({ date: dateLabel, rawDate: reading.date, value: reading.bmi });
    }
    if (reading.blood_glucose !== null && reading.blood_glucose !== undefined) {
      bloodGlucose.push({ date: dateLabel, rawDate: reading.date, value: reading.blood_glucose });
    }
  }

  // Backfill the weight series with the previous weights captured before the
  // window. Weights are recorded infrequently, so without this the trend often
  // shows only one point. These are always older than the in-window readings, so
  // we just add them and re-sort by date.
  for (const pw of priorWeights) {
    if (pw.is_struck) continue;
    if (pw.weight !== null && pw.weight !== undefined) {
      weight.push({ date: formatDateForChart(pw.date), rawDate: pw.date, value: pw.weight });
    }
  }
  if (priorWeights.length > 0) {
    weight.sort((a, b) => (a.rawDate ?? a.date).localeCompare(b.rawDate ?? b.date));
  }

  const latestBP = bloodPressure.length > 0
    ? `${bloodPressure[bloodPressure.length - 1].value}/${bloodPressure[bloodPressure.length - 1].value2}`
    : "\u2014";
  const latestHR = heartRate.length > 0
    ? `${heartRate[heartRate.length - 1].value}`
    : "\u2014";
  const latestTemp = temperature.length > 0
    ? `${temperature[temperature.length - 1].value}\u00B0F`
    : "\u2014";
  const latestO2 = oxygenSaturation.length > 0
    ? `${oxygenSaturation[oxygenSaturation.length - 1].value}%`
    : "\u2014";
  const latestWeight = weight.length > 0
    ? `${weight[weight.length - 1].value} lbs`
    : "\u2014";
  const latestResp = respiration.length > 0
    ? `${respiration[respiration.length - 1].value}`
    : "\u2014";
  const latestPain = painLevel.length > 0
    ? `${painLevel[painLevel.length - 1].value}/10`
    : "\u2014";
  const latestBMI = bmi.length > 0
    ? `${bmi[bmi.length - 1].value}`
    : "\u2014";
  const latestBG = bloodGlucose.length > 0
    ? `${bloodGlucose[bloodGlucose.length - 1].value} mg/dL`
    : "\u2014";

  return {
    bloodPressure,
    heartRate,
    temperature,
    oxygenSaturation,
    weight,
    respiration,
    painLevel,
    bmi,
    bloodGlucose,
    latestBP,
    latestHR,
    latestTemp,
    latestO2,
    latestWeight,
    latestResp,
    latestPain,
    latestBMI,
    latestBG,
    bpTrend: computeTrend(bloodPressure),
    hrTrend: computeTrend(heartRate),
    tempTrend: computeTrend(temperature),
    o2Trend: computeTrend(oxygenSaturation),
    weightTrend: computeTrend(weight),
    respTrend: computeTrend(respiration),
    painTrend: computeTrend(painLevel),
    bmiTrend: computeTrend(bmi),
    bgTrend: computeTrend(bloodGlucose),
  };
}

export const VitalSignsTrendsSection: React.FC<VitalSignsTrendsSectionProps> = ({
  data,
  isLoading,
  error,
}) => {
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="py-4 text-center text-muted-foreground">
          Loading vital signs trends...
        </div>
      );
    }

    if (error) {
      return (
        <div className="py-4 text-center text-destructive">
          Error loading vital signs: {error.message}
        </div>
      );
    }

    const priorWeights = data?.prior_weights ?? [];

    if (!data || (data.readings.length === 0 && priorWeights.length === 0)) {
      return (
        <div className="py-4 text-center text-muted-foreground">
          No vital signs data available
        </div>
      );
    }

    const charts = transformVitalSigns(data.readings, priorWeights);

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {VITALS_VISIBILITY.bloodPressure && (
          <VitalSignTrendChart
            title="Blood Pressure"
            latestValue={charts.latestBP}
            trendDirection={charts.bpTrend}
            data={charts.bloodPressure}
            isDualLine={true}
            line1Label="Previous 7 Days"
            line2Label="Last 3 Days"
            inCarePlan={true}
          />
        )}

        {VITALS_VISIBILITY.heartRate && (
          <VitalSignTrendChart
            title="Heart Rate"
            latestValue={charts.latestHR}
            trendDirection={charts.hrTrend}
            data={charts.heartRate}
            yAxisLabel="bpm"
            line1Label="Previous 7 Days"
            line2Label="Last 3 Days"
            inCarePlan={true}
          />
        )}

        {VITALS_VISIBILITY.temperature && (
          <VitalSignTrendChart
            title="Temperature"
            latestValue={charts.latestTemp}
            trendDirection={charts.tempTrend}
            data={charts.temperature}
            yAxisLabel="°F"
            line1Label="Previous 7 Days"
            line2Label="Last 3 Days"
            inCarePlan={true}
          />
        )}

        {VITALS_VISIBILITY.oxygenSaturation && (
          <VitalSignTrendChart
            title="Oxygen Saturation"
            latestValue={charts.latestO2}
            trendDirection={charts.o2Trend}
            data={charts.oxygenSaturation}
            yAxisLabel="%"
            line1Label="Previous 7 Days"
            line2Label="Last 3 Days"
            inCarePlan={false}
          />
        )}

        {VITALS_VISIBILITY.weight && (
          <VitalSignTrendChart
            title="Weight"
            latestValue={charts.latestWeight}
            trendDirection={charts.weightTrend}
            data={charts.weight}
            yAxisLabel="lbs"
            // Weights are captured infrequently and backfilled from before the
            // window, so this chart can span weeks/months. Use generic labels
            // rather than the day-scoped ones used by the other vitals.
            line1Label="Earlier"
            line2Label="Recent"
            inCarePlan={false}
          />
        )}

        {VITALS_VISIBILITY.respiration && (
          <VitalSignTrendChart
            title="Respiration"
            latestValue={charts.latestResp}
            trendDirection={charts.respTrend}
            data={charts.respiration}
            yAxisLabel="brpm"
            line1Label="Previous 7 Days"
            line2Label="Last 3 Days"
            inCarePlan={false}
          />
        )}

        {VITALS_VISIBILITY.painLevel && (
          <VitalSignTrendChart
            title="Pain Level"
            latestValue={charts.latestPain}
            trendDirection={charts.painTrend}
            data={charts.painLevel}
            yAxisLabel="/10"
            line1Label="Previous 7 Days"
            line2Label="Last 3 Days"
            inCarePlan={false}
          />
        )}

        {VITALS_VISIBILITY.bmi && (
          <VitalSignTrendChart
            title="BMI"
            latestValue={charts.latestBMI}
            trendDirection={charts.bmiTrend}
            data={charts.bmi}
            line1Label="Previous 7 Days"
            line2Label="Last 3 Days"
            inCarePlan={false}
          />
        )}

        {VITALS_VISIBILITY.bloodGlucose && (
          <VitalSignTrendChart
            title="Blood Glucose"
            latestValue={charts.latestBG}
            trendDirection={charts.bgTrend}
            data={charts.bloodGlucose}
            yAxisLabel="mg/dL"
            line1Label="Previous 7 Days"
            line2Label="Last 3 Days"
            inCarePlan={false}
          />
        )}
      </div>
    );
  };

  return (
    <div className="mb-6">
      {/* Section Header */}
      <div className="flex items-center gap-2 mb-4">
        <Activity size={20} className="text-[#228189]" />
        <h2 className="text-lg font-bold text-gray-900">
          Vital Signs Trends <span className="text-sm font-normal text-gray-600">(Recent Trend)</span>
        </h2>
      </div>

      {renderContent()}
    </div>
  );
};

export default VitalSignsTrendsSection;
